import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Store, KeyRound, MessagesSquare, Bot, Code2, Activity, ShieldCheck, LogOut, Menu, X, Columns3,
} from "lucide-react";
import { VerifyBanner } from "@/components/VerifyBanner";
import { Logo } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/app/catalog", label: "API Marketplace", icon: Store, id: "catalog" },
  { to: "/app/providers", label: "Provider & Keys", icon: KeyRound, id: "providers" },
  { to: "/app/playground", label: "AI Playground", icon: MessagesSquare, id: "playground" },
  { to: "/app/compare", label: "Modellvergleich", icon: Columns3, id: "compare" },
  { to: "/app/assistants", label: "KI-Assistenten", icon: Bot, id: "assistants" },
  { to: "/app/api-access", label: "API-Zugang", icon: Code2, id: "api-access" },
  { to: "/app/usage", label: "Nutzung", icon: Activity, id: "usage" },
];

const SideNav = ({ isAdmin, onNav }) => (
  <nav className="flex flex-col gap-1">
    {[...NAV, ...(isAdmin ? [{ to: "/app/admin", label: "Admin", icon: ShieldCheck, id: "admin" }] : [])].map((n) => (
      <NavLink
        key={n.to} to={n.to} end={n.end} onClick={onNav} data-testid={`nav-${n.id}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
            isActive ? "bg-blue-500/15 text-white shadow-[inset_2px_0_0_#4f86ff]" : "text-slate-400 hover:bg-white/5 hover:text-white"
          }`
        }
      >
        <n.icon className="h-4 w-4" />
        {n.label}
      </NavLink>
    ))}
  </nav>
);

const SystemStatus = () => {
  const [ok, setOk] = useState(null);
  useEffect(() => { api.get("/health").then(() => setOk(true)).catch(() => setOk(false)); }, []);
  return (
    <div className="glass p-3 text-xs" data-testid="system-status">
      <div className={`flex items-center gap-2 font-semibold ${ok ? "text-emerald-300" : ok === false ? "text-red-300" : "text-slate-400"}`}>
        <span className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-400 pulse-dot" : ok === false ? "bg-red-400" : "bg-slate-500"}`} />
        {ok ? "Gateway online" : ok === false ? "Gateway nicht erreichbar" : "Prüfe…"}
      </div>
      <div className="mt-1 text-slate-500">Free-First-Routing aktiv</div>
    </div>
  );
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col justify-between border-r border-white/5 bg-[#070c1d]/80 p-5 sticky top-0 h-screen">
        <div className="space-y-8">
          <Logo sub />
          <SideNav isAdmin={isAdmin} />
        </div>
        <SystemStatus />
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 lg:hidden" onClick={() => setOpen(false)}>
          <aside className="h-full w-72 bg-[#070c1d] p-5 space-y-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><Logo /><button data-testid="mobile-nav-close" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button></div>
            <SideNav isAdmin={isAdmin} onNav={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-40 flex items-center justify-between gap-4 border-b border-white/5 bg-[#060a18]/80 px-4 py-3 backdrop-blur-xl sm:px-8">
          <button className="lg:hidden" data-testid="mobile-nav-open" onClick={() => setOpen(true)} aria-label="Menü"><Menu className="h-5 w-5" /></button>
          <div className="hidden sm:block text-xs text-slate-500">Anfragen an externe Provider verlassen den Hub – lokale Modelle (Ollama/vLLM) nicht.</div>
          <div className="flex items-center gap-3">
            <div className="text-right leading-tight">
              <div className="text-sm font-semibold text-white" data-testid="header-user-name">{user?.name}</div>
              <div className="text-xs text-slate-400" data-testid="header-user-role">{isAdmin ? "Administrator" : "Nutzer"}</div>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full btn-grad text-sm">{(user?.name || "?")[0].toUpperCase()}</div>
            <button onClick={doLogout} className="btn-ghost p-2" data-testid="logout-button" aria-label="Abmelden"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <VerifyBanner />
        <main className="px-4 py-8 sm:px-8 lg:px-10 max-w-[1500px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
